import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, Plus } from 'lucide-react';

const AGG_PREFIX = {
  avg: 'Average',
  count: 'Count of',
  count_distinct: 'Distinct',
  min: 'Minimum',
  max: 'Maximum',
  sum: 'Total',
};

/**
 * Derived Measure dialog — extracted from SemanticModelsPage.
 * One-click measure creator: pick aggregation + name, hit create.
 */
export function DerivedMeasureDialog({
  open, onOpenChange,
  selectedEntityData,
  derivedSourceField,
  derivedAgg, setDerivedAgg,
  derivedName, setDerivedName,
  onCreate,
  onResetForm,
}) {
  const sourceAttr = selectedEntityData
    && (selectedEntityData.attributes || []).find(a => a.name === derivedSourceField);
  const sourceLabel = sourceAttr ? (sourceAttr.description || sourceAttr.name) : derivedSourceField;

  const handleAggChange = (v) => {
    setDerivedAgg(v);
    if (derivedSourceField && sourceAttr) {
      const prefix = AGG_PREFIX[v] || '';
      setDerivedName(`${prefix} ${sourceAttr.description || sourceAttr.name}`.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v && onResetForm) onResetForm(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-base">Derived Measure</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 border border-border/30">
            <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-medium text-foreground">{sourceLabel}</p>
              <p className="text-[9px] font-mono text-muted-foreground/70">{derivedSourceField}</p>
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Aggregation</Label>
            <Select value={derivedAgg} onValueChange={handleAggChange}>
              <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sum">SUM - Total</SelectItem>
                <SelectItem value="avg">AVG - Average</SelectItem>
                <SelectItem value="count">COUNT - Count</SelectItem>
                <SelectItem value="count_distinct">COUNT DISTINCT</SelectItem>
                <SelectItem value="min">MIN - Minimum</SelectItem>
                <SelectItem value="max">MAX - Maximum</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Measure Name</Label>
            <Input
              value={derivedName}
              onChange={e => setDerivedName(e.target.value)}
              placeholder="e.g. Average Premium"
              className="rounded-lg bg-muted/50 border-border/50 mt-0.5"
              data-testid="derived-name"
            />
          </div>
          {derivedSourceField && derivedName && (
            <div className="p-2 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
              <p className="text-xs text-foreground">
                <span className="font-medium">{derivedName}</span> = <span className="font-mono text-indigo-400">{derivedAgg.toUpperCase()}</span>(<span className="font-mono text-muted-foreground">{derivedSourceField}</span>)
              </p>
            </div>
          )}
          <Button
            onClick={onCreate}
            disabled={!derivedSourceField || !derivedName.trim()}
            className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
            data-testid="confirm-derived-measure"
          >
            <Plus className="w-4 h-4 mr-2" />Create Measure
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
